import React, { useState } from 'react';
import corporateProfileService from '../services/corporateProfileService';
import './OhipProfilePicker.css';

const TYPE_LABELS = { COM: 'Company', AGT: 'Travel Agent', SRC: 'Source' };
const TYPE_OPTIONS = [
    { value: '',            label: 'All types' },
    { value: 'Company',     label: 'Company' },
    { value: 'TravelAgent', label: 'Travel Agent' },
    { value: 'Source',      label: 'Source' },
];

// onSelect receives { profileType, corporateId, name, profileId }
// Pass either ppId (PP) or searchFn={(name, type) => Promise} (PMM or other)
//
// arAccounts — search OPERA AR ACCOUNTS instead of profiles (Payment Porter Direct Bill rules, which may only bill a
// profile with an active AR account). Each result is an account with the profile it belongs to; restricted accounts are
// listed, so the user sees why a company cannot be chosen, but cannot be picked. onSelect then receives
// { profileId, name, accountNo, accountName }.
const OhipProfilePicker = ({ ppId, searchFn, onSelect, buttonLabel = 'Search OHIP', arAccounts = false }) => {
    const [open, setOpen]           = useState(false);
    const [search, setSearch]       = useState('');
    const [profileType, setProfileType] = useState('');
    const [loading, setLoading]     = useState(false);
    const [results, setResults]     = useState(null);
    const [error, setError]         = useState(null);

    const handleOpen = () => {
        setOpen(true);
        setSearch('');
        setResults(null);
        setError(null);
    };

    const handleSearch = async () => {
        if (!search.trim()) return;
        setLoading(true);
        setError(null);
        setResults(null);
        try {
            if (arAccounts) {
                const res = await corporateProfileService.searchOhipArAccounts(ppId, search.trim());
                setResults(res.data?.accounts || []);
                return;
            }
            const res = searchFn
                ? await searchFn(search.trim(), profileType)
                : await corporateProfileService.searchOhipProfiles(ppId, search.trim(), profileType);
            setResults(res.data?.profiles || []);
        } catch (err) {
            setError(err.response?.data?.error?.message || (arAccounts ? 'Failed to search AR accounts' : 'Failed to search OHIP profiles'));
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (profile) => {
        if (arAccounts) {
            if (!profile.active) return;
            onSelect({ profileId: profile.profileId, name: profile.profileName || profile.accountName,
                accountNo: profile.accountNo, accountName: profile.accountName });
        } else {
            onSelect(profile);
        }
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
    };

    return (
        <>
            <button type="button" className="opp-btn" onClick={handleOpen}>
                {buttonLabel}
            </button>

            {open && (
                <div className="opp-backdrop" onClick={() => setOpen(false)}>
                    <div className="opp-modal" onClick={e => e.stopPropagation()}>
                        <div className="opp-modal-header">
                            <span className="opp-modal-title">{arAccounts ? 'Search AR Accounts' : 'Search OHIP Profiles'}</span>
                            <button type="button" className="opp-close" onClick={() => setOpen(false)}>×</button>
                        </div>

                        <div className="opp-search-bar">
                            <input
                                className="opp-search-input"
                                type="text"
                                placeholder="Enter name to search…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                            />
                            {!arAccounts && (
                                <select
                                    className="opp-type-select"
                                    value={profileType}
                                    onChange={e => setProfileType(e.target.value)}
                                >
                                    {TYPE_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            )}
                            <button
                                type="button"
                                className="opp-search-btn"
                                onClick={handleSearch}
                                disabled={loading || !search.trim()}
                            >
                                {loading ? '…' : 'Search'}
                            </button>
                        </div>

                        <div className="opp-modal-body">
                            {loading && <p className="opp-status">Searching OHIP…</p>}
                            {error   && <p className="opp-error">{error}</p>}
                            {!loading && results !== null && results.length === 0 && (
                                <p className="opp-status">No {arAccounts ? 'AR accounts' : 'profiles'} found for "{search}".</p>
                            )}
                            {!loading && results === null && !error && (
                                <p className="opp-status">Enter a name and click Search.</p>
                            )}
                            {!loading && results && arAccounts && results.map((a, i) => (
                                <button
                                    key={`${a.accountNo}-${i}`}
                                    type="button"
                                    className={`opp-result${a.active ? '' : ' opp-result-disabled'}`}
                                    onClick={() => handleSelect(a)}
                                    disabled={!a.active}
                                    title={a.active ? undefined : `Not active in OPERA${a.restrictedReason ? ` — ${a.restrictedReason}` : ' (restricted)'}`}
                                >
                                    <span className={`opp-type-badge ${a.active ? 'opp-ar-active' : 'opp-ar-restricted'}`}>
                                        {a.active ? 'Active' : 'Restricted'}
                                    </span>
                                    <span className="opp-result-name">{a.accountName}</span>
                                    <span className="opp-result-id">AR {a.accountNo}{a.profileId ? ` · profile ${a.profileId}` : ''}</span>
                                </button>
                            ))}
                            {!loading && results && !arAccounts && results.map((p, i) => (
                                <button
                                    key={`${p.profileId || p.corporateId}-${i}`}
                                    type="button"
                                    className="opp-result"
                                    onClick={() => handleSelect(p)}
                                >
                                    <span className={`opp-type-badge opp-type-${p.profileType?.toLowerCase()}`}>
                                        {TYPE_LABELS[p.profileType] || p.profileType}
                                    </span>
                                    <span className="opp-result-name">{p.name}</span>
                                    {p.corporateId && <span className="opp-result-id">{p.corporateId}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OhipProfilePicker;
